import React, { useEffect, useMemo, useRef, useState } from 'react';
import comms from '../../comms';
import { sendCreateItineraryFlagTrip } from './itinerarySocketHelper';
import { useFlagTripQuote } from '../FlagTrip/useFlagTripQuote';
import { computeMileageAndDuration, friendlyServerError } from '../FlagTrip/computeTravel';
import FlagTripPrepayPanel from '../FlagTrip/FlagTripPrepayPanel';
import LocationSearchBox from '../FlagTrip/LocationSearchBox';
import { getRecentDestinations, recordDestinationUse } from '../FlagTrip/recentDestinations';

/**
 * FlagTripCreateModal — Phase B itinerary-mode flag trip.
 *
 * Pickup = driver's current GPS (passenger already on board → trip created Loaded).
 * Dropoff is a Google Places address — required or optional per the selected rate's
 * destinationRequired policy (docs/DriverRequirement/03_create_and_flag_trip.md). Rate
 * selection, quoting, and pre-trip payment collection are shared with LiveDispatch's
 * FlagTripModal via useFlagTripQuote; only the final creation call is itinerary-specific
 * (createItineraryFlagTrip vs. Live's extended FLAGM).
 *
 * Server contract (see 03_flag_trip_client_handoff.md): ITINERARY_ACTION senders for
 * rates/quote/create, replies on flagTripRates, flagTripQuote, flagTripCreated.
 */

const FlagTripCreateModal = ({
  isOpen,
  onClose,
  routeId,
  vehicleId,
  driverId,
  currentLocation, // { lat, lng } — pickup origin
  fleetSettings,
  googleMapsLoaded,
  onSuccess,
}) => {
  const [destination, setDestination] = useState(null); // {address,lat,lon,streetNumber,...}
  const [wantsDestination, setWantsDestination] = useState(false); // explicit "Add destination" when optional
  const [ambulatoryCount, setAmbulatoryCount] = useState(1);
  const [wheelchairCount, setWheelchairCount] = useState(0);
  const [travel, setTravel] = useState({ mileage: 0, durationSeconds: 0 });
  const [quotedOrigin, setQuotedOrigin] = useState(null); // origin snapshot the current quote was priced from
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [rawServerError, setRawServerError] = useState(null); // raw server payload, surfaced under a debug flag
  const [resolvedOrigin, setResolvedOrigin] = useState(null); // self-acquired GPS fix when the parent has none yet
  const [originStatus, setOriginStatus] = useState('idle'); // 'idle' | 'locating' | 'ready' | 'error'

  // localStorage.flagTripDebug === '1' surfaces the raw server error in the modal so
  // it can be screenshotted/relayed to the backend team without opening DevTools.
  let debugOn = false;
  try { debugOn = localStorage.getItem('flagTripDebug') === '1'; } catch (e) { debugOn = false; }
  const lastSubmitRef = useRef(0);
  const SUBMIT_COOLDOWN_MS = 5000;

  // Reset whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setDestination(null);
    setWantsDestination(false);
    setAmbulatoryCount(1);
    setWheelchairCount(0);
    setTravel({ mileage: 0, durationSeconds: 0 });
    setQuotedOrigin(null);
    setIsSubmitting(false);
    setError(null);
    setRawServerError(null);
    setResolvedOrigin(null);
    setOriginStatus('idle');
  }, [isOpen]);

  // Pickup origin = the driver's live location. Prefer the parent's vehiclePosition;
  // if it isn't available yet (GPS still acquiring, or testing on desktop), acquire a
  // one-time fix here so the price/fit check isn't blocked.
  useEffect(() => {
    if (!isOpen) return;
    if (currentLocation && currentLocation.lat != null && currentLocation.lng != null) {
      setOriginStatus('ready');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setOriginStatus('error');
      return;
    }
    setOriginStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setResolvedOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setOriginStatus('ready');
      },
      () => setOriginStatus('error'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
    );
  }, [isOpen, currentLocation]);

  // Effective pickup origin: parent GPS first, then the self-acquired fix.
  const origin =
    currentLocation && currentLocation.lat != null && currentLocation.lng != null
      ? currentLocation
      : resolvedOrigin;

  const {
    rates, ratesError, selectedRateId, selectedRate,
    quote, isQuoting, quoteError, requestQuote, invalidateQuote: invalidateQuoteBase,
    prepay, requestPrepay, checkPrepayStatus, isCheckingPrepay,
  } = useFlagTripQuote({
    isOpen,
    mode: 'itinerary',
    vehicleId,
    originLat: currentLocation?.lat,
    originLon: currentLocation?.lng,
  });

  // Also drop the frozen quote-time origin snapshot whenever the quote itself is invalidated —
  // otherwise a stale snapshot could linger and get reused for an unrelated later quote.
  const invalidateQuote = () => {
    invalidateQuoteBase();
    setQuotedOrigin(null);
  };

  // Trip-creation events remain itinerary-specific (not part of the shared hook, which only
  // covers rates/quote/prepay — final creation differs per mode).
  useEffect(() => {
    if (!isOpen) return undefined;
    const socket = comms.getSocket();
    if (!socket) return undefined;
    const logRaw = (label, payload) => {
      console.error(label, payload);
      try { setRawServerError(`${label}\n${JSON.stringify(payload, null, 2)}`); }
      catch (err) { setRawServerError(`${label}\n${String(payload)}`); }
    };
    const onCreated = (c) => {
      setIsSubmitting(false);
      if (c?.success) {
        if (onSuccess) onSuccess(c);
        onClose();
      } else {
        logRaw('[FlagTrip] flagTripCreated error payload:', c);
        setError(friendlyServerError(c?.error, 'Could not create the trip.'));
      }
    };
    const onItinError = (e) => {
      if (e?.action === 'createItineraryFlagTrip') {
        setIsSubmitting(false);
        logRaw('[FlagTrip] itinerary_error (create):', e);
        setError(friendlyServerError(e?.error, 'Could not create the trip.'));
      }
    };
    socket.on('flagTripCreated', onCreated);
    socket.on('itinerary_error', onItinError);
    return () => {
      socket.off('flagTripCreated', onCreated);
      socket.off('itinerary_error', onItinError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // A specific street is only a suggestion here, not a requirement — LocationSearchBox
  // runs in advisory mode (strict={false}) so a vague pick is still accepted and only
  // draws a hint. A cleared field arrives as null.
  const handleDestinationChange = (dest) => {
    setDestination(dest);
    invalidateQuote();
    setError(null);
    if (dest) recordDestinationUse(driverId, dest);
  };

  // Quick-pick recent destinations — same UX as RiderApp's starred address chips
  // (RiderApp - 1/src/pages/NewTrip.jsx pickupChips/dropoffChips), scoped per driver.
  const destinationChips = useMemo(
    () => (driverId ? getRecentDestinations(driverId).slice(0, 3) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [driverId, isOpen]
  );

  // Default to "required" while no rate is selected yet, so the field still shows up front —
  // matches the previous always-required behavior until a rate's real policy is known.
  const destinationRequired = selectedRate ? !!selectedRate.destinationRequired : true;

  const canQuote =
    !!selectedRateId &&
    (!destinationRequired || !!destination) &&
    !!origin &&
    origin.lat != null &&
    origin.lng != null &&
    !isQuoting;

  const handleCheckPrice = async () => {
    if (!canQuote) return;
    setError(null);
    let t = travel;
    if (destination) {
      t = await computeMileageAndDuration(origin, destination, fleetSettings);
      setTravel(t);
    }
    // Freeze the exact origin used for this quote — the server signs quoteToken with a
    // fingerprint of these inputs (lib/flagTripPolicy.js canonicalFingerprint/verifyQuoteToken).
    // `origin` here is recomputed on every render from the live currentLocation prop, so
    // submitting with whatever it evaluates to at Create-trip time (a later, drifted GPS fix)
    // fails verification with "Quote inputs have changed" even though nothing the driver
    // controls actually changed — Submit must reuse this exact snapshot instead.
    setQuotedOrigin({ lat: origin.lat, lng: origin.lng });
    requestQuote({
      routeId,
      vehicleId,
      // Must match what Submit sends — the server signs quoteToken with a fingerprint
      // that includes passengerCount (derived server-side from these two counts when
      // no explicit passengerCount is given; lib/flagTripPolicy.js canonicalFingerprint).
      // Omitting them here made the server default to passengerCount:1 at quote time,
      // which diverged from Submit's real counts and failed verification as soon as
      // either count differed from the 1/0 defaults.
      ambulatoryCount: parseInt(ambulatoryCount, 10) || 0,
      wheelchairCount: parseInt(wheelchairCount, 10) || 0,
      mileage: t.mileage,
      durationSeconds: t.durationSeconds,
      originLat: origin.lat,
      originLon: origin.lng,
      destinationLat: destination?.lat,
      destinationLon: destination?.lon,
    });
  };
  const paymentTiming = quote?.paymentTiming;
  const needsPrepay = paymentTiming === 'before_submit';

  // Hard gate (per spec): only when priced AND fits AND (no pre-submit payment due, or settled).
  const canSubmit =
    !!quote &&
    quote.success === true &&
    quote.allowable === true &&
    typeof quote.price === 'number' &&
    (!needsPrepay || prepay.status === 'settled') &&
    !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (!routeId || !vehicleId) {
      setError('Vehicle or route not available.');
      return;
    }
    const now = Date.now();
    if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
      setError('Please wait a moment before submitting again.');
      return;
    }
    lastSubmitRef.current = now;
    setIsSubmitting(true);
    setError(null);
    const submitOrigin = quotedOrigin || origin;
    sendCreateItineraryFlagTrip({
      routeId,
      vehicleId,
      driverId,
      rateId: selectedRateId,
      rateName: rates.find((r) => r.id === selectedRateId)?.name,
      quoteToken: quote.quoteToken,
      prepayRef: needsPrepay ? prepay.prepayRef : undefined,
      ambulatoryCount: parseInt(ambulatoryCount, 10) || 0,
      wheelchairCount: parseInt(wheelchairCount, 10) || 0,
      passengerName: 'Flag Trip',
      originLat: submitOrigin.lat,
      originLon: submitOrigin.lng,
      originTime: new Date().toISOString(),
      destinationLat: destination?.lat,
      destinationLon: destination?.lon,
      destinationStreetNumber: destination?.streetNumber,
      // An establishment (airport, hospital, hotel) has no "route" component, so
      // streetName is empty for it. AD skips destination street-segment creation when
      // BOTH streetName and destinationLandmark are blank (dispatch.js §"destination
      // street segment"), which would leave the trip with coordinates but no readable
      // dropoff. Send the venue name as the landmark in that case — the same slot AD
      // already accepts — and fall back to Google's formatted address.
      destinationStreetName: destination?.streetName,
      destinationLandmark: destination?.streetName
        ? undefined
        : (destination?.placeName || destination?.address || undefined),
      destinationCity: destination?.city,
      destinationState: destination?.state,
      destinationPostalCode: destination?.postalCode,
      durationSeconds: travel.durationSeconds,
      mileage: travel.mileage,
      tripPrice: quote.price,
    });
  };

  if (!isOpen) return null;

  const stepBtn =
    'w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-600 flex items-center justify-center text-2xl font-bold text-white transition-colors disabled:opacity-50';

  return (
    <div className="fixed inset-0 z-[2200] flex items-center justify-center bg-black bg-opacity-50">
      <div className="m-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between bg-amber-500 px-6 py-4">
          <h2 className="text-xl font-semibold text-white">Flag Trip</h2>
          <button onClick={onClose} disabled={isSubmitting} className="text-white hover:text-gray-200">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
          {(error || ratesError || quoteError) && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error || ratesError || quoteError}
            </div>
          )}
          {debugOn && rawServerError && (
            <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-900 p-2 text-[10px] leading-snug text-amber-200">
              {rawServerError}
            </pre>
          )}

          <p className="text-xs text-gray-500">
            Pickup is your current location (passenger on board).
          </p>

          {/* Rate — resolved automatically (Flag Trip rate, or the fleet's only active
              rate); NO picker shown to the driver, per docs/DriverRequirement/
              03_create_and_flag_trip.md: "the rate is resolved by the server, never
              chosen by the driver." */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Rate</label>
            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {selectedRate ? `${selectedRate.name}${selectedRate.code ? ` (${selectedRate.code})` : ''}` : 'Resolving rate…'}
            </div>
          </div>

          {/* Dropoff address — required or optional per the selected rate's policy */}
          {selectedRateId && (
            <div>
              {!destinationRequired && !wantsDestination ? (
                <button
                  type="button"
                  onClick={() => setWantsDestination(true)}
                  className="text-sm font-semibold text-amber-600 hover:underline"
                >
                  + Add destination (optional)
                </button>
              ) : (
                <>
                  <LocationSearchBox
                    googleMapsLoaded={googleMapsLoaded}
                    // `origin`, not the raw currentLocation prop: it falls back to the
                    // self-acquired GPS fix, so suggestions stay biased on desktop or
                    // while the parent's vehiclePosition is still unavailable.
                    currentLocation={origin}
                    value={destination}
                    onChange={handleDestinationChange}
                    recentPlaces={destinationChips}
                    label="Dropoff address"
                    placeholder="Search dropoff street address"
                    required={destinationRequired}
                    accent="amber"
                    strict={false}
                  />
                </>
              )}
            </div>
          )}

          {/* Capacity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Ambulatory</label>
              <div className="flex items-center justify-center gap-3">
                <button className={stepBtn} disabled={ambulatoryCount <= 0} onClick={() => { setAmbulatoryCount((n) => Math.max(0, n - 1)); invalidateQuote(); }}>-</button>
                <span className="w-8 text-center text-2xl font-bold">{ambulatoryCount}</span>
                <button className={stepBtn} onClick={() => { setAmbulatoryCount((n) => n + 1); invalidateQuote(); }}>+</button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Wheelchair</label>
              <div className="flex items-center justify-center gap-3">
                <button className={stepBtn} disabled={wheelchairCount <= 0} onClick={() => { setWheelchairCount((n) => Math.max(0, n - 1)); invalidateQuote(); }}>-</button>
                <span className="w-8 text-center text-2xl font-bold">{wheelchairCount}</span>
                <button className={stepBtn} onClick={() => { setWheelchairCount((n) => n + 1); invalidateQuote(); }}>+</button>
              </div>
            </div>
          </div>

          {/* Check price / fit */}
          <button
            onClick={handleCheckPrice}
            disabled={!canQuote}
            className="w-full rounded-lg border border-amber-500 px-4 py-2.5 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50"
          >
            {isQuoting ? 'Checking…' : 'Check price & fit'}
          </button>

          {/* Why the button is disabled (so it isn't a silent grey button) */}
          {!canQuote && !isQuoting && (
            <p className="-mt-2 text-center text-xs text-gray-500">
              {!selectedRateId
                ? 'Resolving rate…'
                : destinationRequired && !destination
                ? 'Enter a dropoff address to continue.'
                : originStatus === 'locating'
                ? 'Getting your current location…'
                : 'Your location is unavailable — enable location access to price the trip.'}
            </p>
          )}

          {/* Quote result */}
          {quote && quote.success && quote.allowable && typeof quote.price === 'number' && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{quote.priceFinal ? 'Price' : 'Estimated price'}</span>
                <span className="text-lg font-bold">${Number(quote.price).toFixed(2)}</span>
              </div>
              {travel.mileage > 0 && (
                <div className="mt-1 text-xs text-green-700">
                  ~{travel.mileage.toFixed(1)} mi · fits before next pickup
                </div>
              )}
            </div>
          )}
          {quote && quote.success && quote.allowable === false && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {quote.error || 'This trip does not fit before the next scheduled pickup.'}
            </div>
          )}

          {needsPrepay && quote?.success && quote.allowable && (
            <>
              <FlagTripPrepayPanel
                prepay={prepay}
                accentColor="#D97706"
                onCheckStatus={checkPrepayStatus}
                isChecking={isCheckingPrepay}
              />
              {prepay.status === 'idle' && (
                <button
                  type="button"
                  onClick={() => requestPrepay({ driverId, routeId })}
                  className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-600"
                >
                  Collect payment
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-amber-500 px-6 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Creating…' : 'Create trip'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagTripCreateModal;
